/**
 * Where a backsplash band's dragged top edge snaps, and what it saves (call
 * item #28). Pure.
 *
 * A snap is where one of the Backsplash card's buttons puts the band — the
 * setting's LINE, read from the band builder's own inputs (`wallBandLines`,
 * evaluated by `counterLineForSpec` / `rangeTopForSpec`), never a number of
 * this module's. Anywhere else is the card's Custom height: whole inches
 * above the counter, the way the setting measures it. Heights here are WORLD
 * Y metres; the drawing crosses into its own frame with `bandHeightFrame`.
 */
import {
  wallBandLines,
  joinRoomOpenings,
} from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import {
  counterLineForSpec,
  rangeTopForSpec,
  readBacksplashSpec,
  vanityBandHeightM,
  tubBandHeightM,
} from '@shared-lib/kitchen-assembly/styling/backsplashSpec';
import { SHOWER_TILE_STANDARD_HEIGHT_IN } from '@shared-lib/geometry/showerWallTiling';
import { WALL_REGION_SNAP_DISTANCE_IN } from './wallRegionSnapLines';
import { nudgeStep } from './edgeNudge';
import {
  bathroomBandLines,
  bathroomBandOf,
  wallBathroomBands,
} from './bathroomBandCard';

const INCH_M = 0.0254;
/** How close a dragged edge must come to a line to land on it — the 3D
 * Edit-wall section drag's own pull. */
const SNAP_M = WALL_REGION_SNAP_DISTANCE_IN * INCH_M;
/** The bands a top edge can be dragged on — every band of the card. */
export const DRAGGABLE_BANDS = Object.freeze([
  'counter',
  'range',
  'vanity',
  'tub',
  'shower',
]);

/** Two lines closer than this are one place to land. */
const SAME_LINE_M = 0.5 * INCH_M;

// The card's buttons, in its order, with the value each writes.
const COUNTER_BUTTONS = [
  { chip: 'splash4', name: '4"', value: { stop: 'inches', inches: 4 } },
  { chip: 'cabinets', name: 'To the cabinets', value: { stop: 'cabinets' } },
  { chip: 'full', name: 'Full height', value: { stop: 'full' } },
  { chip: 'sill', name: 'To the sill', value: { stop: 'sill' } },
];
// The bathroom card's buttons (`BathroomBandsCard`), in its order.
const VANITY_BUTTONS = [
  { chip: 'in4', name: '4"', value: { stop: 'inches', inches: 4 } },
  { chip: 'mirror', name: 'To the mirror', value: { stop: 'mirror' } },
];
const TUB_BUTTONS = [
  { chip: 'in4', name: '4"', value: { stop: 'inches', inches: 4 } },
  { chip: 'in12', name: '12"', value: { stop: 'inches', inches: 12 } },
  { chip: 'full', name: 'To the ceiling', value: { stop: 'full' } },
];
// The shower's two heights — the values `apply_shower_tiling` takes. There
// is no custom shower height: the cloud tiler cuts the tile.
const SHOWER_BUTTONS = [
  { chip: 'standard', name: 'Standard (7′)', value: 'standard' },
  { chip: 'full', name: 'Full height', value: 'full' },
];

const RANGE_BUTTONS = [
  { chip: 'counter', name: 'Like the counter', value: { stop: 'counter' } },
  { chip: 'hood', name: 'To the hood', value: { stop: 'hood' } },
  { chip: 'full', name: 'To the ceiling', value: { stop: 'full' } },
];

/**
 * Is `a` the same setting as `b`? A custom 4" is the 4" chip (one value);
 * `{stop, inches: null}` (as `readBacksplashSpec` reads it) is `{stop}`; a
 * shower height is a string.
 */
export function sameBandValue(a, b) {
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (a.stop !== b.stop) return false;
  return a.stop !== 'inches' || Number(a.inches) === Number(b.inches);
}

/**
 * Keep one snap per line: where two land within ½″ of each other, the one
 * the wall is set to now (or shows by default) wins, else the earlier in the
 * card's order.
 */
function oneSnapPerLine(snaps, preferred) {
  const out = [];
  for (const snap of snaps) {
    const twin = out.findIndex((s) => Math.abs(s.y - snap.y) < SAME_LINE_M);
    if (twin < 0) out.push(snap);
    else if (sameBandValue(snap.value, preferred)) out[twin] = snap;
  }
  return out;
}

const snapsFrom = (buttons, lineOf) =>
  buttons.map((b) => ({ ...b, y: lineOf(b.value) }));

/**
 * What a band's top edge can be dragged to on one wall, in world Y: the
 * snaps (`{chip, name, value, y}` — the card's buttons), the base a custom
 * height is measured from, the limits, the wall's current setting
 * (`current`, null when it has none — the automatic band), and the value the
 * card shows for it (`shown`: the setting, else the card's default chip).
 *
 * @param {object} room the room as stored; slim openings are joined
 * @param {string} wallId
 * @param {'counter'|'range'} band
 * @param {{structureOpenings?: object}} [opts]
 * @returns {?{baseY, minY, maxY, current, shown, snaps, unit}} null when the wall
 *   has nothing of that band to drag (off, no counter, no range)
 */
export function bandHeightOptions(
  room,
  wallId,
  band,
  { structureOpenings, regionId = null } = {}
) {
  const wall = (room?.walls || []).find((w) => w.id === wallId);
  if (!wall) return null;
  if (band === 'vanity' || band === 'tub' || band === 'shower') {
    return bathroomOptions(room, wallId, band, regionId);
  }
  const spec = readBacksplashSpec(wall);
  const lines = wallBandLines(
    joinRoomOpenings(room, structureOpenings),
    wallId
  );

  if (band === 'counter' && lines.counter) {
    const at = lines.counter;
    const offered = COUNTER_BUTTONS.filter(
      (b) => b.chip !== 'sill' || at.sillY != null
    );
    const preferred = spec.counter ?? { stop: 'cabinets' };
    return {
      baseY: at.counterTopY,
      minY: at.counterTopY + INCH_M,
      maxY: at.wallTopY,
      current: spec.counter,
      shown: preferred,
      snaps: oneSnapPerLine(
        snapsFrom(offered, (v) => counterLineForSpec(v, at).lineY),
        preferred
      ),
      unit: 'counter',
    };
  }
  if (band === 'range' && lines.range) {
    const at = lines.range;
    const hasHood = at.hoodBottoms.length > 0;
    const offered = RANGE_BUTTONS.filter((b) => b.chip !== 'hood' || hasHood);
    // the card's default under a hood is To the hood, else Like the counter
    const preferred =
      spec.range ?? (hasHood ? { stop: 'hood' } : { stop: 'counter' });
    return {
      baseY: at.counterTopY,
      minY: at.counterTopY + INCH_M,
      // an over-range cabinet or microwave stops every range setting
      maxY: Math.min(at.wallTopY, ...at.capBottoms),
      current: spec.range,
      shown: preferred,
      snaps: oneSnapPerLine(
        snapsFrom(offered, (v) => rangeTopForSpec(v, at)),
        preferred
      ),
      unit: 'counter',
    };
  }
  return null;
}

/**
 * A bathroom band's options (`bandHeightOptions`): the card's buttons at the
 * heights the vanity / tub rule runs them to (`vanityBandHeightM`,
 * `tubBandHeightM` — the computes' own), a custom height measured up from the
 * vanity's counter or the tub's surface; the shower's two heights from the
 * foot of its tile.
 */
function bathroomOptions(room, wallId, band, regionId) {
  const lines = bathroomBandLines(room, wallId, band, regionId);
  if (!lines) return null;
  const facts = wallBathroomBands(room, wallId)?.[band];
  const { baseY, wallTopY } = lines;
  const limits = { baseY, minY: baseY + INCH_M, maxY: wallTopY };
  if (band === 'shower') {
    const standardY = Math.min(
      baseY + SHOWER_TILE_STANDARD_HEIGHT_IN * INCH_M,
      wallTopY
    );
    const current = facts?.tileHeight ?? null;
    const shown = current ?? 'standard';
    return {
      ...limits,
      current,
      shown,
      snaps: oneSnapPerLine(
        SHOWER_BUTTONS.map((b) => ({
          ...b,
          y: b.value === 'full' ? wallTopY : standardY,
        })),
        shown
      ),
      unit: 'shower',
    };
  }
  const current = facts?.spec ?? null;
  if (band === 'tub') {
    const at = { tubTopY: baseY, wallTopY };
    const shown = current ?? { stop: 'inches', inches: 12 };
    return {
      ...limits,
      current,
      shown,
      snaps: oneSnapPerLine(
        snapsFrom(TUB_BUTTONS, (v) => baseY + tubBandHeightM(v, at)),
        shown
      ),
      unit: 'tub',
    };
  }
  const at = {
    counterTopY: baseY,
    mirrorBottomY: lines.mirrorBottomY,
    wallTopY,
  };
  const offered = VANITY_BUTTONS.filter(
    (b) => b.chip !== 'mirror' || lines.mirrorBottomY != null
  );
  const shown = current ?? { stop: 'inches', inches: 4 };
  return {
    ...limits,
    current,
    shown,
    snaps: oneSnapPerLine(
      snapsFrom(offered, (v) => baseY + vanityBandHeightM(v, at)),
      shown
    ),
    unit: 'counter',
  };
}

/**
 * Where the wall's own setting runs the band — what a drag starts from, so a
 * press that barely moves keeps the setting rather than snapping to the line
 * of the piece it landed on (a piece an upper stops sits on the To the
 * cabinets line). Null with no setting: then the drag starts from the band as
 * drawn.
 */
export function currentLineY(options) {
  return lineYOf(options, options?.current);
}

/**
 * Where the card's SHOWN value runs the band — the wall's own setting, else
 * the chip the card lights by default (To the cabinets, 12″ over a tub, …).
 * What an arrow-key nudge starts from: there is no pressed piece to start
 * from, and the card is showing this value.
 */
export function shownLineY(options) {
  return lineYOf(options, options?.current ?? options?.shown);
}

/** The line a band value runs to: its button's, or a Custom height's. */
function lineYOf(options, value) {
  if (!value) return null;
  const snap = options.snaps.find((s) => sameBandValue(s.value, value));
  if (snap) return snap.y;
  if (value.stop === 'inches') {
    return Math.min(options.baseY + value.inches * INCH_M, options.maxY);
  }
  return null;
}

const landedOn = ({ chip, name, value, y }) => ({
  value,
  snap: { chip, name },
  y,
});

/**
 * The setting one ↑ / ↓ press (PR 4) moves a band's top edge to from world
 * height `fromY`: the next whole inch above `baseY` — a Custom height — or the
 * card button whose line it reaches on the way (`nudgeStep`), held between
 * the options' limits. The shower has its two heights only: up is Full
 * height, down is Standard.
 *
 * @returns {{value, snap: ?{chip, name}, y: number}} as `changeAtHeight`
 */
export function nudgeChange(fromY, options, direction) {
  if (options.unit === 'shower') {
    const byHeight = [...options.snaps].sort((a, b) => a.y - b.y);
    return landedOn(
      direction > 0 ? byHeight[byHeight.length - 1] : byHeight[0]
    );
  }
  const { baseY, minY, maxY, snaps } = options;
  const { at, line } = nudgeStep({
    from: fromY,
    base: baseY,
    min: minY,
    max: maxY,
    lines: snaps.map((s) => s.y),
    direction,
  });
  if (line >= 0) return landedOn(snaps[line]);
  // a Custom height is whole inches, inside the limits
  const lowest = Math.ceil((minY - baseY) / INCH_M - 1e-6);
  const highest = Math.floor((maxY - baseY) / INCH_M + 1e-6);
  const inches = Math.min(
    Math.max(Math.round((at - baseY) / INCH_M), lowest),
    highest
  );
  return {
    value: { stop: 'inches', inches },
    snap: null,
    y: baseY + inches * INCH_M,
  };
}

/**
 * The setting a band's top edge dragged to world height `y` writes: the
 * nearest snap within 3″ (unless `skipSnaps` — Shift held), else a custom
 * height in whole inches (at least 1) above `baseY`. `y` is held between the
 * options' limits first.
 *
 * @returns {{value, snap: ?{chip, name}, y: number}} `y` where the edge sits
 */
export function changeAtHeight(y, options, { skipSnaps = false } = {}) {
  const held = Math.min(Math.max(y, options.minY), options.maxY);
  // The shower has its two heights only: the nearer, whatever Shift says.
  if (options.unit === 'shower') {
    const nearest = options.snaps.reduce((best, s) =>
      Math.abs(s.y - held) < Math.abs(best.y - held) ? s : best
    );
    const { chip, name, value } = nearest;
    return { value, snap: { chip, name }, y: nearest.y };
  }
  if (!skipSnaps) {
    let best = null;
    for (const s of options.snaps) {
      const d = Math.abs(s.y - held);
      if (d <= SNAP_M && (!best || d < best.d)) best = { s, d };
    }
    if (best) {
      const { chip, name, value } = best.s;
      return { value, snap: { chip, name }, y: best.s.y };
    }
  }
  const inches = Math.max(1, Math.round((held - options.baseY) / INCH_M));
  return {
    value: { stop: 'inches', inches },
    snap: null,
    y: options.baseY + inches * INCH_M,
  };
}

/**
 * How far a pointer's vertical travel moves the edge, in metres — up the
 * screen is up the wall. `pxPerMetre` already includes the view's zoom
 * (`RenderWallElevation`'s `pxPerMetreRef`).
 */
export const dragDeltaM = (dyPx, pxPerMetre) =>
  pxPerMetre > 0 ? -dyPx / pxPerMetre : 0;

const isBandRegion = (r) =>
  r?.surfaceType === 'backsplash' || !!bathroomBandOf(r);
const bandShape = (wall) =>
  JSON.stringify({
    spec: wall?.backsplashSpec ?? null,
    bands: (wall?.texture?.regions || [])
      .filter(isBandRegion)
      .map((r) =>
        [r.surfaceType, r.kind ?? '', r.x, r.y, r.width, r.height]
          .map((v) => (typeof v === 'number' ? v.toFixed(2) : v))
          .join(':')
      )
      .sort(),
  });

/**
 * The walls of `after` whose bands or setting differ from `before` — what a
 * planned change actually moves. Compared by shape (to 0.01 %), not by
 * identity: a rebuild mints new region ids for a wall it redraws the same.
 */
export function changedWallIds(before, after) {
  const was = new Map((before || []).map((w) => [w.id, w]));
  return (after || [])
    .filter((w) => bandShape(w) !== bandShape(was.get(w.id)))
    .map((w) => w.id);
}
